import cssesc from 'cssesc';
import pick from 'lodash/pick';
import {Component} from 'panel';

import {abbreviateNumber, commaizeNumber, truncateToElement} from 'common/util';
import {useEventListener} from 'common/util/component-helpers';
import {pluralize, truncateMiddle} from 'common/util/string';
import {formatConciseDuration} from 'common/util/date';
import {Section} from 'common/components/items-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {emitTrackEvent} from 'common/util/dom';
import * as colors from 'common/util/colors';
import {ChartLegendTheme} from 'common/components/chart-legend/constants';

import {Alignment, SankeyNodeType} from './models/types';
import SANKEY_CONFIG from './sankey-config.json';
import template from './index.jade';
import css from './index.styl?inline';
import {SankeyStep} from './models/sankey-step';

const COMPONENT_NAME = `mp-sankey-chart`;
interface SankeyState {
  activeColumn: number | null;
  labelsCropped: boolean;
  links: any[] | null;
  lastColor: number;
  segmentColors: UnknownObj;
  selectedNode: number | null;
  tooltip: UnknownObj | null;
  topPath: Set<any>;
  removeStep: SankeyStep;
}

export class SankeyChart extends Component<SankeyState> {
  private tableElement;
  private nodeHeightFactor;
  private _data;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alignbreakdowns: `boolean`,
      alwaysbreakdown: `boolean`,
      'can-decrease-cardinality': `boolean`,
      'tooltip-enabled': `boolean`,
      reverse: `boolean`,
      'disable-add-step': `boolean`,
      'disable-anchor-event-labels': `boolean`,
      'event-rename': {type: `json`, default: []},
      events: {type: `json`, default: []},
      alignment: {type: `json`, default: []},
      'scroll-offset': `number`,
    };
  }

  constructor() {
    super();

    this.onNodeClicked = this.onNodeClicked.bind(this);
    this.clearSelectedNode = this.clearSelectedNode.bind(this);
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        activeColumn: null,
        labelsCropped: false,
        links: null,
        lastColor: 0,
        segmentColors: {},
        selectedNode: null,
        tooltip: null,
        topPath: new Set(),
        removeStep: null,
      },

      helpers: {
        abbreviateNumber,
        commaizeNumber,
        formatConciseDuration,
        pluralize,
        ChartLegendTheme,

        isEmptyFlow: () => {
          return !this.data.steps.length || this.data.steps.every((step) => !step.nodes.length);
        },
        nodeLabelMinHeight: SANKEY_CONFIG.MIN_LABEL_HEIGHT,
        getAlignmentIcon: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? `caret-left` : `caret-right`;
        },
        getToggledAlignment: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? Alignment.RIGHT : Alignment.LEFT;
        },
        getJumpIcon: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? `jump-right` : `jump-left`;
        },
        getDefaultAlignment: (section: number) => {
          return section === 0 ? Alignment.RIGHT : Alignment.LEFT;
        },
        getFlowAnchorStep: () => this.data.getFirstAnchorStep(),
        getNodeAttributes: (node, step) => this.getNodeAttributes(node, step),
        isInRemoveRange: (step) => this.isInRemoveRange(step),
        getBreakdownSections: (): Section[] => {
          const items = this.data.breakdowns.map((breakdown) => {
            const {color, name} = this.getBreakdownAttributes(breakdown);
            return {color, label: name, isSelected: true};
          });
          return [{items}];
        },
        getBreakdownAttributes: (breakdown) => this.getBreakdownAttributes(breakdown),
        getBottomMarginPadding: (nodeHeight, isLastNode) => this.getBottomMarginPadding(nodeHeight, isLastNode),
        onNodeClicked: (ev) => this.onNodeClicked(ev),
        onRemoveStepHover: (ev) => this.onRemoveStepHover(ev),
        onColumnHeaderClick: (columnIndex) => this.update({activeColumn: columnIndex}),
        renameEvents: (name) => {
          const idx = parseInt(name, 10);
          if (isNaN(idx)) {
            return name;
          } else {
            return this.attr(`event-rename`)[idx] || name;
          }
        },
        truncateLabel: (label, maxChar) => {
          return truncateMiddle(label, maxChar);
        },
        updateAlignment: ({section, alignment}) => {
          this.dispatchEvent(new CustomEvent(`updateAlignment`, {composed: true, detail: {section, alignment}}));
        },
        updateSteps: ({operation, type, anchor, absolute}) => {
          const {removeStep} = this.state;
          if (removeStep) {
            this.update({
              removeStep: null,
            });
          }
          this.dispatchEvent(
            new CustomEvent(`updateSteps`, {
              composed: true,
              detail: {operation, type, anchor, absolute, scrollOffset: this.getScrollPosition()},
            }),
          );
        },
        getAbsoluteSteps: ({step, operation, value}) => {
          let absoluteSteps: number;
          const delta = operation === `remove` ? -value : value;
          if (step.isReverse) {
            absoluteSteps = step.anchorStepIndex - step.step + delta;
          } else {
            absoluteSteps = step.step - step.anchorStepIndex + delta;
          }
          return absoluteSteps;
        },
        getAddStepsSections: () => [
          {
            items: [
              {label: `Add 1 Step`, value: 1},
              {label: `Add 3 Steps`, value: 3},
              {label: `Add 5 Steps`, value: 5},
            ],
          },
        ],
        closeContextMenu: () => this.dispatchEvent(new CustomEvent(`contextMenuClosed`, {composed: true})),
        showContextMenu: (ev, node) => {
          let menuItems: unknown[] = [
            {value: `cardinatlity_add`, icon: `plus`, label: `Add a row of events`},
            {
              value: `cardinatlity_remove`,
              icon: `circle-clear`,
              label: `Remove a row of events`,
              isDisabled: !this.attr(`can-decrease-cardinality`),
            },
          ];

          if (!node.isSecondaryPart) {
            menuItems = [
              {value: `breakdown`},
              {value: `hide`, isDisabled: node.type === SankeyNodeType.Anchor},
              ...menuItems,
            ];
          }

          this.dispatchEvent(
            new CustomEvent(`contextMenuOpen`, {
              detail: {
                ev,
                contextMenu: {
                  node: pick(node, [`event`, `step`]),
                  items: menuItems,
                },
              },
              composed: true,
            }),
          );
        },

        truncateHeader: (vnode) =>
          requestAnimationFrame(() => {
            vnode.elm.textContent = truncateToElement(vnode.elm.textContent, vnode.elm);
          }),

        // updates the current hovered edge to display its data
        onMouseLeave: () => this.update({tooltip: null}),
        onMouseMove: (ev) => {
          const {tooltip} = this.state;
          const {left: parentLeft, top: parentTop} = ev.currentTarget.getBoundingClientRect();
          const top = ev.clientY - parentTop;
          const left = ev.clientX - parentLeft;
          const {edge, breakdown} = ev.target;

          if (edge && this.attr(`tooltip-enabled`)) {
            this.update({
              tooltip: {
                breakdown,
                edge,
                left,
                top,
              },
            });
          } else if (!edge && tooltip) {
            this.update({tooltip: null});
          }
        },

        // calculate link shapes once all nodes are in place
        onTableInserted: (vnode) =>
          requestAnimationFrame(() => {
            this.tableElement = vnode.elm;
            this.scrollToOffset(this.attr(`scroll-offset`));
            this.calculateFlowLinks();
          }),

        onTablePatched: () =>
          requestAnimationFrame(() => {
            if (!this.state.labelsCropped) {
              this.cropNodeLabels();
            }
          }),
      },
    };
  }

  get data() {
    return this._data;
  }

  set data(data) {
    if (data.steps) {
      this._data = data;
      this.onDataChange();
    }
  }

  onDataChange() {
    // calculates the height/step weight ratio of the most spacious step
    // this allows to fit the column with most nodes inside the boundaries
    // of the chart and use that ratio as the global scale for the chart
    const stepHeights = this.data.steps.map(
      (step) => (SANKEY_CONFIG.PLOT_SIZE - step.nodes.length * SANKEY_CONFIG.PLOT_NODE_PADDING) / step.weight,
    );
    this.nodeHeightFactor = Math.min(...stepHeights);

    // update links if data was updated once the
    // chart has been rendered
    if (this.tableElement) {
      this.update({labelsCropped: false});
      requestAnimationFrame(() => this.calculateFlowLinks());
    }

    this.state.lastColor = 0;
  }

  attributeChangedCallback(name: string, oldValue: null | string, newValue: null | string): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (this.data && this.data.steps) {
      this.onDataChange();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    useEventListener(this, window, `resize`, () =>
      requestAnimationFrame(() => {
        if (this.tableElement) {
          this.calculateFlowLinks();
        }
      }),
    );
    this.onDisconnected(() => {
      document.removeEventListener(`click`, this.clearSelectedNode);
    });
  }

  onRemoveStepHover(step) {
    const {removeStep} = this.state;

    if (!step) {
      return;
    }

    if (removeStep) {
      this.update({
        removeStep: null,
      });
    } else {
      this.update({
        removeStep: step,
      });
    }
  }

  /**
   * Toggle selection and calculates top path on node click
   */
  onNodeClicked(clickEvent) {
    const {selectedNode} = this.state;
    const {node} = clickEvent.currentTarget;

    if (!node) {
      return;
    }

    // toggle current selected node
    if (selectedNode === node.id) {
      this.clearSelectedNode();
    } else {
      this.update({
        selectedNode: node.id,
        topPath: this.data.calculateTopFlowPath(node),
      });

      emitTrackEvent(this, {
        eventName: `Highlight node`,
        eventPrefix: null,
        props: {
          'Event name': node.eventString,
          'Step number': node.step,
        },
      });

      clickEvent.stopPropagation();
      document.addEventListener(`click`, this.clearSelectedNode);
      this.calculateFlowLinks();
    }
  }

  /**
   * Resets top path selection
   */
  clearSelectedNode() {
    document.removeEventListener(`click`, this.clearSelectedNode);

    this.update({
      selectedNode: null,
      topPath: new Set(),
    });

    this.calculateFlowLinks();
  }

  isInRemoveRange(step) {
    const {removeStep} = this.state;
    let removeRange = false;
    if (!!removeStep && removeStep.isReverse) {
      removeRange =
        step.anchor === removeStep.anchor && step.section === removeStep.section && step.step <= removeStep.step;
    } else if (!!removeStep && removeStep.isForward) {
      removeRange =
        step.anchor === removeStep.anchor && step.section === removeStep.section && step.step >= removeStep.step;
    }
    return removeRange;
  }

  /**
   * Generates attributes for a given node shape and its label
   */
  getNodeAttributes(node, step) {
    const {topPath, removeStep} = this.state;
    const heights = (node.breakdowns || [node.totalCount]).map((b) => Math.ceil(b.totalCount * this.nodeHeightFactor));
    const height = heights.reduce((a, b) => a + b, 0);
    const isInTopPath = node.edges.concat(node.incomingEdges).some((edge) => topPath.has(edge.edgeId));
    const nodeType = node.type.toLowerCase();
    const isInRemoveRange = !!removeStep && (removeStep.nodes.includes(node) || this.isInRemoveRange(step));

    return {
      heights,
      height,
      isInTopPath,
      isInRemoveRange,
      nodeType,
    };
  }

  getBreakdownAttributes(breakdownEl) {
    const name = breakdownEl.segments.map((s) => s.values.join(` & `)).join(`/`);
    const color = this.getBreakdownColor(breakdownEl);
    const height = Math.ceil(breakdownEl.totalCount * this.nodeHeightFactor);
    const flowsTotalCount = this.data.breakdowns.reduce((total, breakdown) => total + breakdown.totalCount, 0);

    return {
      flowsTotalCount,
      name,
      color,
      height,
    };
  }

  getBottomMarginPadding(nodeHeight, isLastNode) {
    const totalNodeHeight = nodeHeight + SANKEY_CONFIG.PLOT_NODE_PADDING;
    const minTotalNodeHeight = SANKEY_CONFIG.MIN_LABEL_HEIGHT + SANKEY_CONFIG.NODE_LABEL_MARGIN + 8;
    let marginPadding =
      totalNodeHeight <= minTotalNodeHeight ? minTotalNodeHeight - nodeHeight : SANKEY_CONFIG.PLOT_NODE_PADDING;
    if (isLastNode) {
      marginPadding = totalNodeHeight < minTotalNodeHeight ? minTotalNodeHeight - nodeHeight : 0;
    }
    return marginPadding;
  }

  getBreakdownColor(breakdown) {
    if (!Object.prototype.hasOwnProperty.call(this.state.segmentColors, breakdown.id)) {
      this.state.segmentColors[breakdown.id] = colors.getChartSegmentColorCombo(this.state.lastColor++).primary;
    }

    return this.state.segmentColors[breakdown.id];
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  calculateFlowLinks() {
    const links = [];
    const element = this.tableElement;
    const columnElement = `.sankey-table-column.is-step-column`;
    const columnWidth = element.querySelector(columnElement).offsetWidth;
    const fetchNodeElement = (id) => element.querySelector(`.sankey-node[data-id='${cssesc(id)}']`);

    // determines the horizontal offset for each beizer curve control points
    const beizerCurveDelta = columnWidth * SANKEY_CONFIG.CURVY_FACTOR;

    const {selectedNode} = this.state;

    // First initialize the from and to node offsets from the top of the node at which each edge starts.
    // Deltas are stored as objects because we want to pass by reference.
    const fromNodeTopDeltas = {};
    const toNodeTopDeltas = {};
    const fromNodeBreakdownTopDeltas = {};
    const toNodeBreakdownTopDeltas = {};
    for (const step of this.data.steps) {
      for (const node of step.nodes) {
        fromNodeTopDeltas[node.id] = {value: 0};
        toNodeTopDeltas[node.id] = {value: 0};

        const breakdownOffset = {value: 0};
        for (const breakdown of node.breakdowns) {
          if (!Object.prototype.hasOwnProperty.call(fromNodeBreakdownTopDeltas, node.id)) {
            fromNodeBreakdownTopDeltas[node.id] = {};
          }

          fromNodeBreakdownTopDeltas[node.id][breakdown.id] = {value: breakdownOffset.value};

          if (!Object.prototype.hasOwnProperty.call(toNodeBreakdownTopDeltas, node.id)) {
            toNodeBreakdownTopDeltas[node.id] = {};
          }

          toNodeBreakdownTopDeltas[node.id][breakdown.id] = {value: breakdownOffset.value};

          breakdownOffset.value += breakdown.totalCount * this.nodeHeightFactor;
        }
      }
    }

    // Calculate the starting and ending coordinates for the edges. Edges from the same breakdown are grouped
    // together so that they align with the cohort breakdown in the node.
    for (const step of this.data.steps) {
      for (const node of step.nodes) {
        const fromNode = fetchNodeElement(node.id);

        // Now for each edge withing a breakdown calculate its coordinates based on the offset found above,
        // Each offset is increased by the vertical size of the current edge in preparation for the next one.
        for (const edge of node.edges) {
          const toNode = fetchNodeElement(edge.id);
          let edgeCreated = false;
          for (const breakdown of edge.breakdowns) {
            const edgeHeight = breakdown.totalCount * this.nodeHeightFactor;
            let fromNodeTopDelta;
            let toNodeTopDelta;
            // In the case we want to show the breakdowns always or there is only one breakdown which is the default case
            // when not comparing cohorts both the from and to node have the edges aligned to their breakdowns always.
            if (this.attr(`alwaysbreakdown`) || this.data.breakdowns.length === 1) {
              fromNodeTopDelta = fromNodeBreakdownTopDeltas[node.id][breakdown.id];
              toNodeTopDelta = toNodeBreakdownTopDeltas[edge.id][breakdown.id];
              // If the from node is selected then the edges will only be aligned to the breakdowns in the from node.
            } else if (selectedNode && edge.id === selectedNode) {
              fromNodeTopDelta = fromNodeTopDeltas[node.id];
              toNodeTopDelta = toNodeBreakdownTopDeltas[edge.id][breakdown.id];
              // If the from node is selected then the edges will only be aligned to the breakdowns in the from node.
            } else if (selectedNode && node.id === selectedNode) {
              fromNodeTopDelta = fromNodeBreakdownTopDeltas[node.id][breakdown.id];
              toNodeTopDelta = toNodeTopDeltas[edge.id];
              // If this edge is not broken down stop.
            } else {
              break;
            }

            if (!this.attr(`alignbreakdowns`)) {
              // If we got here that means we want to breakdown this edge, but we don't want it to be aligned.
              fromNodeTopDelta = fromNodeTopDeltas[node.id];
              toNodeTopDelta = toNodeTopDeltas[edge.id];
            }

            const edgeShapeData = this.generateEdgeShapeData({
              beizerCurveDelta,
              edgeHeight,
              fromNode,
              fromNodeTopDelta: fromNodeTopDelta.value,
              toNode,
              toNodeTopDelta: toNodeTopDelta.value,
            });
            links.push(this.generateEdgeConfiguration(edge, breakdown, edgeShapeData));
            fromNodeTopDelta.value += edgeHeight;
            toNodeTopDelta.value += edgeHeight;
            edgeCreated = true;
          }
          // The edge is not broken down.
          if (!edgeCreated) {
            const edgeHeight = edge.totalCount * this.nodeHeightFactor;
            const edgeShapeData = this.generateEdgeShapeData({
              beizerCurveDelta,
              edgeHeight,
              fromNode,
              fromNodeTopDelta: fromNodeTopDeltas[node.id].value,
              toNode,
              toNodeTopDelta: toNodeTopDeltas[edge.id].value,
            });
            links.push(this.generateEdgeConfiguration(edge, null, edgeShapeData));
            fromNodeTopDeltas[node.id].value += edgeHeight;
            toNodeTopDeltas[edge.id].value += edgeHeight;
          }
        }
      }
    }

    this.update({links});
  }

  /**
   * Generates configuration for an edge by checking
   * if the given edge is currently highlighted
   */
  generateEdgeConfiguration(edge, breakdown, edgeShapeData) {
    const {topPath} = this.state;
    const color = breakdown ? this.getBreakdownColor(breakdown) : colors.mpColors[`$gray3`];

    return {
      class: {
        'is-secondary': edge.fromNode.isSecondaryPart || edge.isSecondaryPart,
        'is-drop-off': edge.fromNode.isDropOff || edge.isDropOff,
        'is-in-top-path': topPath.has(edge.edgeId),
      },
      color,
      props: {edge, breakdown},
      attrs: {d: edgeShapeData.join(` `)},
    };
  }

  /**
   * Generates configuration for an edge to/from a breakdown legend node.
   */
  generateBreakdownEdgeConfiguration(breakdown, edgeShapeData) {
    return {
      class: {'is-breakdown': true},
      color: this.getBreakdownColor(breakdown),
      attrs: {d: edgeShapeData.join(` `)},
    };
  }

  /**
   * Generates a curved SVG path data for an edge
   */
  generateEdgeShapeData({fromNode, toNode, edgeHeight, fromNodeTopDelta, toNodeTopDelta, beizerCurveDelta}) {
    const fromNodeMiddle = fromNode.offsetLeft + fromNode.offsetWidth / 2;
    const fromNodeRight = fromNodeMiddle + fromNode.offsetWidth / 2;
    const fromNodeTop = fromNode.offsetTop + fromNodeTopDelta;

    const toNodeMiddle = toNode.offsetLeft + toNode.offsetWidth / 2;
    const toNodeLeft = toNodeMiddle - toNode.offsetWidth / 2;
    const toNodeTop = toNode.offsetTop + toNodeTopDelta;

    const fromNodeBottom = fromNodeTop + edgeHeight;
    const toNodeBottom = toNodeTop + edgeHeight;

    return [
      `M`,
      fromNodeMiddle,
      fromNodeTop, // fromNode top middle
      `H`,
      fromNodeRight, // fromNode top right
      `C`,
      fromNodeRight + beizerCurveDelta,
      fromNodeTop,
      toNodeLeft - beizerCurveDelta,
      toNodeTop,
      toNodeLeft,
      toNodeTop, // top beizer curve
      `H`,
      toNodeMiddle, // toNode top middle
      `V`,
      toNodeBottom, // toNode bottom middle
      `H`,
      toNodeLeft, // toNode bottom left
      `C`,
      toNodeLeft - beizerCurveDelta,
      toNodeBottom,
      fromNodeRight + beizerCurveDelta,
      fromNodeBottom,
      fromNodeRight,
      fromNodeBottom, // bottom beizer curve
      `H`,
      fromNodeMiddle, // fromNode bottom middle
      `z`,
    ];
  }

  /**
   * Crops all node labels in the chart, resets the cropped state
   * before doing the crop calculation so re-renders are not cropped
   * when they need not to be
   */
  cropNodeLabels() {
    if (!this.tableElement) {
      return;
    }

    const labels = this.tableElement.querySelectorAll(`.sankey-node-label`);
    labels.forEach((label) => {
      label.classList.remove(`is-cropped`, `is-loaded`);
      label.style.flex = ``;
      this.cropNodeLabel(label);
    });

    this.update({labelsCropped: true});
  }

  scrollToOffset(scrollOffset: number) {
    const scrollerElement = this.tableElement.parentElement;
    scrollerElement.scrollLeft = scrollOffset;
  }

  getScrollPosition() {
    const scrollerElement = this.tableElement.parentElement;
    return scrollerElement.scrollLeft;
  }

  /**
   * Dynamically crops a node label based on the node's height
   * and the rendered height of the label
   */
  cropNodeLabel(labelElement) {
    const labelHeight = labelElement.offsetHeight;
    const nodeHeight = labelElement.nodeAttributes.height;
    const nodeHeightDelta = labelHeight - nodeHeight;

    if (labelHeight > SANKEY_CONFIG.MIN_LABEL_HEIGHT && nodeHeightDelta >= SANKEY_CONFIG.NODE_LABEL_MARGIN) {
      labelElement.classList.add(`is-center_aligned`, `is-cropped`, `is-loaded`);
      labelElement.style.flex = `auto`;
    }
  }
}

export const SankeyChartEl = defineAndMakeJsxFactory(COMPONENT_NAME, SankeyChart);
